import { YearFilter } from '../components/YearFields';
import { formatDating, validateYearFilters } from '../utils/dating';
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight, Search, SlidersHorizontal, X } from 'lucide-react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import ScrollControls from '../components/ScrollControls';
import SearchableSelect from '../components/SearchableSelect';
import ThumbnailImage from '../components/ThumbnailImage';
import { useLanguage } from '../hooks/useLanguage';
import {
  localiseCategoryName,
  localiseDetailSubtypeName,
  localiseDetailTypeName,
  localiseManufacturerName,
} from '../i18n/filterTranslations';

const copy = {
  RU: {
    kicker: 'КАТАЛОГ', title: 'Поиск по архиву', intro: 'Ищите по номеру, году, производителю, типу детали и описанию.',
    placeholder: 'Номер, год, описание или тип', find: 'Найти', all: 'Везде', products: 'Экземпляры', details: 'Детали',
    settings: 'Настройки каталога', hideSettings: 'Закрыть настройки', apply: 'Применить', reset: 'Сбросить',
    yearFrom: 'С года', yearTo: 'По год включительно', years: 'Годы', year: 'Год', manufacturer: 'Производитель', belongsTo: 'Категория', type: 'Тип детали', subtype: 'Разновидность',
    any: 'Любое значение', sort: 'Сортировка', size: 'Элементов на странице', found: 'Найдено',
    recent: 'Добавлены недавно', oldest: 'Добавлены давно', yearDesc: 'Год: поздние', yearAsc: 'Год: ранние', numberAsc: 'Номер: по возрастанию', numberDesc: 'Номер: по убыванию', relevance: 'По релевантности',
    productsGroup: 'Экземпляры в сборе', detailsGroup: 'Детали', empty: 'По этим условиям ничего не найдено.',
    loading: 'Ищем в архиве…', error: 'Не удалось получить результаты.', previous: 'Назад', next: 'Вперёд',
    productionYear: 'Год выпуска', serial: 'Серийный номер', description: 'Описание', source: 'Источник',
    subtypeLabel: 'Разновидность', active: 'Выбрано', clear: 'Очистить', photo: 'Фотография',
  },
  EN: {
    kicker: 'CATALOGUE', title: 'Search the archive', intro: 'Search by serial number, year, maker, part type or description.',
    placeholder: 'Serial number, year, description or type', find: 'Search', all: 'Everything', products: 'Examples', details: 'Parts',
    settings: 'Catalogue settings', hideSettings: 'Close settings', apply: 'Apply', reset: 'Reset',
    yearFrom: 'From year', yearTo: 'To year (inclusive)', years: 'Years', year: 'Year', manufacturer: 'Maker', belongsTo: 'Category', type: 'Part type', subtype: 'Variant',
    any: 'Any value', sort: 'Sort', size: 'Items per page', found: 'Found',
    recent: 'Recently added', oldest: 'Oldest additions', yearDesc: 'Year: newest', yearAsc: 'Year: earliest', numberAsc: 'Serial number: ascending', numberDesc: 'Serial number: descending', relevance: 'Relevance',
    productsGroup: 'Complete examples', detailsGroup: 'Parts', empty: 'No records match these settings.',
    loading: 'Searching the archive…', error: 'Unable to load results.', previous: 'Previous', next: 'Next',
    productionYear: 'Production year', serial: 'Serial number', description: 'Description', source: 'Source',
    subtypeLabel: 'Variant', active: 'Selected', clear: 'Clear', photo: 'Photograph',
  },
};

const PAGE_SIZES = [20, 40, 60, 100, 250];

function normalisePage(data) {
  if (Array.isArray(data)) return { content: data, totalPages: 1, totalElements: data.length, number: 0 };
  return {
    content: Array.isArray(data?.content) ? data.content : [],
    totalPages: Number.isFinite(data?.totalPages) ? data.totalPages : 1,
    totalElements: Number.isFinite(data?.totalElements) ? data.totalElements : (data?.content?.length || 0),
    number: Number.isFinite(data?.number) ? data.number : 0,
  };
}

function filterEntries(searchParams) {
  const excluded = new Set(['q', 'searchType', 'page', 'sortField', 'sortOrder', 'size']);
  return Object.fromEntries([...searchParams.entries()].filter(([key]) => !excluded.has(key)));
}

function resolveManufacturerOption(options, value) {
  const exact = options.find((option) => option.id === value);
  if (exact || typeof value !== 'string') return exact;
  // Older links can omit an abbreviation's final dot or use different casing.
  const normalise = (name) => name.trim().toLowerCase().replace(/\.+$/, '');
  const matches = options.filter((option) => normalise(option.id) === normalise(value));
  return matches.length === 1 ? matches[0] : undefined;
}

function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, index) => index);
  const values = new Set([0, total - 1, current - 1, current, current + 1]);
  return [...values].filter((value) => value >= 0 && value < total).sort((a, b) => a - b);
}

function RecordCard({ item, product, uploadHost, labels, catalogReturnTo, catalogReturnKey }) {
  const { lang } = useLanguage();
  const title = product
    ? item.name || (item.number ? `№ ${item.number}` : `#${item.id}`)
    : [item.type?.name, item.name].filter(Boolean).join(' — ') || `#${item.id}`;
  const href = product ? `/products/${item.id}` : `/details/${item.id}`;
  const photos = (item.photos || []).slice(0, 8);

  return (
    <article className={product ? 'catalog-record catalog-record--product' : 'catalog-record'}>
      <Link to={href} state={{ catalogReturnTo, catalogReturnKey }} className="catalog-record__main">
        <div className="catalog-record__identity">
          <span>{String(item.id).padStart(5, '0')}</span>
          <h3>{title}</h3>
        </div>

        <dl className="catalog-record__facts">
          {formatDating(item, lang) && <><dt>{labels.productionYear}</dt><dd>{formatDating(item, lang)}</dd></>}
          {String(item.number ?? '').trim() && <><dt>{labels.serial}</dt><dd>{item.number}</dd></>}
          {item.manufacturer?.name && <><dt>{labels.manufacturer}</dt><dd>{item.manufacturer.name}</dd></>}
          {item.subtype?.name && <><dt>{labels.subtypeLabel}</dt><dd>{item.subtype.name}</dd></>}
          {item.description && <><dt>{labels.description}</dt><dd>{item.description}</dd></>}
          {item.source && <><dt>{labels.source}</dt><dd>{item.source}</dd></>}
        </dl>
      </Link>

      {photos.length > 0 && (
        <Link to={href} state={{ catalogReturnTo, catalogReturnKey }} className="catalog-record__photos" aria-label={labels.photo}>
          {photos.map((photo) => {
            const src = photo.url?.startsWith('/') ? uploadHost + photo.url : photo.url;
            return <ThumbnailImage key={photo.id || src} src={src} alt="" />;
          })}
        </Link>
      )}
    </article>
  );
}

function Pagination({ page, totalPages, onPage, labels }) {
  if (totalPages <= 1) return null;
  const visible = pageWindow(page, totalPages);

  return (
    <nav className="catalog-pagination" aria-label="Pagination">
      <button type="button" disabled={page === 0} onClick={() => onPage(page - 1)}><ChevronLeft size={16} />{labels.previous}</button>
      <div>
        {visible.map((value, index) => (
          <React.Fragment key={value}>
            {index > 0 && value - visible[index - 1] > 1 && <span>…</span>}
            <button type="button" className={page === value ? 'is-active' : ''} onClick={() => onPage(value)} aria-current={page === value ? 'page' : undefined}>
              {value + 1}
            </button>
          </React.Fragment>
        ))}
      </div>
      <button type="button" disabled={page >= totalPages - 1} onClick={() => onPage(page + 1)}>{labels.next}<ChevronRight size={16} /></button>
    </nav>
  );
}

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const preserveScroll = location.state?.preserveScroll === true;
  const catalogReturnTo = location.pathname + location.search + location.hash;
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const labels = copy[lang] || copy.RU;

  const urlType = ['details', 'products', 'all'].includes(searchParams.get('searchType')) ? searchParams.get('searchType') : 'all';
  const urlQuery = searchParams.get('q') || '';
  const urlPage = Math.max(0, Number.parseInt(searchParams.get('page') || '0', 10) || 0);
  const urlSize = PAGE_SIZES.includes(Number(searchParams.get('size'))) ? Number(searchParams.get('size')) : 20;
  const urlSortField = searchParams.get('sortField') || (urlQuery.trim() ? 'relevance' : 'id');
  const urlSortOrder = searchParams.get('sortOrder') || 'desc';

  const [query, setQuery] = useState(urlQuery);
  const [searchType, setSearchType] = useState(urlType);
  const [yearFilterReset, setYearFilterReset] = useState(0);
  const [filters, setFilters] = useState(() => filterEntries(searchParams));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [manufacturers, setManufacturers] = useState([]);
  const [types, setTypes] = useState([]);
  const [result, setResult] = useState({ products: [], details: [], productsPage: null, detailsPage: null });
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    setQuery(urlQuery);
    setSearchType(urlType);
    setFilters(filterEntries(searchParams));
  }, [searchParams, urlQuery, urlType]);

  useEffect(() => {
    Promise.all([api.get('/manufacturers'), api.get('/detailTypes')])
      .then(([makers, detailTypes]) => {
        setManufacturers(Array.isArray(makers.data) ? makers.data : []);
        setTypes(Array.isArray(detailTypes.data) ? detailTypes.data : []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    const params = {
      q: urlQuery,
      page: urlPage,
      size: urlSize,
      sortField: urlSortField === 'relevance' && !urlQuery.trim() ? 'id' : urlSortField,
      sortOrder: urlSortField === 'relevance' && !urlQuery.trim() ? 'desc' : urlSortOrder,
      ...filterEntries(searchParams),
    };

    // Keep the current list mounted during inline settings changes so the
    // document does not shrink and force the browser to move the viewport.
    setStatus((current) => preserveScroll && (current === 'ready' || current === 'updating') ? 'updating' : 'loading');
    const load = async () => {
      try {
        if (urlType === 'products') {
          const { data } = await api.get('/products/search', { params });
          const page = normalisePage(data);
          if (active) setResult({ products: page.content, details: [], productsPage: page, detailsPage: null });
        } else if (urlType === 'details') {
          const { data } = await api.get('/details/page', { params });
          const page = normalisePage(data);
          if (active) setResult({ products: [], details: page.content, productsPage: null, detailsPage: page });
        } else {
          const detailsOnly = Boolean(params.type || params.subtype);
          const [{ data }, productResponse] = await Promise.all([
            api.get('/search/all', { params }),
            detailsOnly ? Promise.resolve(null) : api.get('/products/search', { params }).catch(() => null),
          ]);
          const detailsPage = normalisePage(data?.details);
          const productsPage = productResponse ? normalisePage(productResponse.data) : normalisePage(data?.products);
          if (active) setResult({
            products: detailsOnly ? [] : (Array.isArray(data?.products) ? data.products : productsPage.content),
            details: detailsPage.content,
            productsPage,
            detailsPage,
          });
        }
        if (active) setStatus('ready');
      } catch (error) {
        console.error(error);
        if (active) setStatus('error');
      }
    };
    load();
    return () => { active = false; };
  }, [searchParams, urlPage, urlQuery, urlSize, urlSortField, urlSortOrder, urlType, preserveScroll]);

  const belongsToOptions = useMemo(() => [...new Set(types.map((type) => type.belongsTo).filter(Boolean))].map((name) => ({
    id: name,
    name: localiseCategoryName(name, lang),
  })), [lang, types]);
  const manufacturerOptions = useMemo(() => manufacturers.map((maker) => ({
    id: maker.name,
    name: localiseManufacturerName(maker, lang),
  })), [lang, manufacturers]);
  const selectedManufacturer = resolveManufacturerOption(manufacturerOptions, filters.manufacturer);
  const availableTypes = useMemo(() => types.filter((type) => !filters.belongsTo || type.belongsTo === filters.belongsTo), [filters.belongsTo, types]);
  const typeOptions = useMemo(() => availableTypes.map((type) => ({
    id: type.name,
    name: localiseDetailTypeName(type, lang),
  })), [availableTypes, lang]);
  const selectedType = useMemo(() => types.find((type) => type.name === filters.type), [filters.type, types]);
  const subtypeOptions = useMemo(() => (selectedType?.subtypes || []).map((subtype) => ({
    id: subtype.name,
    name: localiseDetailSubtypeName(subtype, lang),
  })), [lang, selectedType]);

  const updateUrl = (overrides = {}, { preserveScroll = false } = {}) => {
    const nextQuery = overrides.query ?? query;
    const nextType = overrides.searchType ?? searchType;
    const nextFilters = overrides.filters ?? filters;
    try { validateYearFilters(nextFilters, lang); } catch (error) { alert(error.message); return false; }
    let sortField = overrides.sortField ?? urlSortField;
    let sortOrder = overrides.sortOrder ?? urlSortOrder;
    if (!nextQuery.trim() && sortField === 'relevance') { sortField = 'id'; sortOrder = 'desc'; }

    const params = new URLSearchParams({
      q: nextQuery,
      searchType: nextType,
      page: String(overrides.page ?? 0),
      sortField,
      sortOrder,
      size: String(overrides.size ?? urlSize),
    });
    Object.entries(nextFilters).forEach(([key, value]) => value && params.set(key, value));
    const canonicalManufacturer = resolveManufacturerOption(manufacturerOptions, nextFilters.manufacturer);
    if (canonicalManufacturer) params.set('manufacturer', canonicalManufacturer.id);
    navigate('/search?' + params.toString(), { state: preserveScroll ? { preserveScroll: true } : null });
    return true;
  };

  const updateFilter = (key, value) => {
    setFilters((current) => {
      const next = { ...current, [key]: value || '' };
      if (key === 'belongsTo') {
        const valid = types.filter((type) => !value || type.belongsTo === value).map((type) => type.name);
        if (!valid.includes(next.type)) { next.type = ''; next.subtype = ''; }
      }
      if (key === 'type') next.subtype = '';
      return next;
    });
  };

  const submit = (event) => {
    event.preventDefault();
    updateUrl({
      page: 0,
      sortField: query.trim() && !searchParams.get('sortField') ? 'relevance' : urlSortField,
      sortOrder: 'desc',
    });
  };

  const changeType = (value) => {
    const nextFilters = { ...filters };
    if (value === 'products') {
      delete nextFilters.type;
      delete nextFilters.subtype;
    }
    if (updateUrl({ searchType: value, filters: nextFilters, page: 0 })) {
      setSearchType(value);
      setFilters(nextFilters);
    }
  };

  const sortOptions = [
    ...(query.trim() ? [['relevance,desc', labels.relevance]] : []),
    ['id,desc', labels.recent], ['id,asc', labels.oldest], ['year,desc', labels.yearDesc], ['year,asc', labels.yearAsc],
    ['number,asc', labels.numberAsc], ['number,desc', labels.numberDesc],
  ];
  const currentSort = sortOptions.some(([value]) => value === `${urlSortField},${urlSortOrder}`) ? `${urlSortField},${urlSortOrder}` : 'id,desc';
  const activeFilters = Object.entries(filters).filter(([, value]) => value);
  const displayFilterValue = (key, value) => {
    if (key === 'manufacturer') return selectedManufacturer?.name || value;
    const optionsByKey = {
      belongsTo: belongsToOptions,
      type: typeOptions,
      subtype: subtypeOptions,
    };
    return optionsByKey[key]?.find((option) => option.id === value)?.name || value;
  };
  const total = (result.productsPage?.totalElements || 0) + (result.detailsPage?.totalElements || 0);
  const totalPages = Math.max(result.productsPage?.totalPages || 0, result.detailsPage?.totalPages || 0, 1);
  const uploadHost = process.env.NODE_ENV === 'development' ? 'http://localhost:8080' : '';
  const showResults = status === 'ready' || status === 'updating';

  return (
    <main className="catalog-page page-width">
      <ScrollControls />
      <header className="catalog-header">
        <p className="section-kicker">{labels.kicker}</p>
        <h1>{labels.title}</h1>
        <p>{labels.intro}</p>
      </header>

      <section className="catalog-workspace" aria-label={labels.title}>
        <form className="catalog-searchbar" onSubmit={submit}>
          <Search size={20} />
          <label className="sr-only" htmlFor="catalog-query">{labels.placeholder}</label>
          <input id="catalog-query" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={labels.placeholder} />
          {query && <button type="button" className="catalog-clear" onClick={() => setQuery('')} aria-label={labels.clear}><X size={16} /></button>}
          <div className="catalog-search-type">
            <select value={searchType} onChange={(event) => changeType(event.target.value)} aria-label={labels.all}>
              <option value="all">{labels.all}</option><option value="products">{labels.products}</option><option value="details">{labels.details}</option>
            </select>
            <ChevronDown size={16} aria-hidden="true" focusable="false" />
          </div>
          <button type="submit" className="catalog-submit">{labels.find}</button>
        </form>

        <div className="catalog-toolbar">
          <button type="button" className={settingsOpen ? 'catalog-settings-button is-active' : 'catalog-settings-button'} onClick={() => setSettingsOpen((value) => !value)}>
            <SlidersHorizontal size={17} />{settingsOpen ? labels.hideSettings : labels.settings}
            {activeFilters.length > 0 && <span>{activeFilters.length}</span>}
          </button>
        </div>

        {settingsOpen && (
          <div className="catalog-settings">
            <div className="catalog-settings__grid">
              <YearFilter key={searchParams.toString() + yearFilterReset} filters={filters} initialFilters={filterEntries(searchParams)} onChange={setFilters} />
              <label><span>{labels.manufacturer}</span><SearchableSelect options={manufacturerOptions} value={selectedManufacturer?.id || filters.manufacturer || null} onChange={(value) => updateFilter('manufacturer', value)} placeholder={labels.any} /></label>
              <label><span>{labels.belongsTo}</span><SearchableSelect options={belongsToOptions} value={filters.belongsTo || null} onChange={(value) => updateFilter('belongsTo', value)} placeholder={labels.any} /></label>
              {searchType !== 'products' && <label><span>{labels.type}</span><SearchableSelect options={typeOptions} value={filters.type || null} onChange={(value) => updateFilter('type', value)} placeholder={labels.any} /></label>}
              {searchType !== 'products' && filters.type && subtypeOptions.length > 0 && <label><span>{labels.subtype}</span><SearchableSelect options={subtypeOptions} value={filters.subtype || null} onChange={(value) => updateFilter('subtype', value)} placeholder={labels.any} /></label>}
              <label><span>{labels.sort}</span>
                <select value={currentSort} onChange={(event) => { const [sortField, sortOrder] = event.target.value.split(','); updateUrl({ sortField, sortOrder, page: 0 }, { preserveScroll: true }); }}>
                  {sortOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
              </label>
              <label><span>{labels.size}</span>
                <select value={urlSize} onChange={(event) => updateUrl({ size: Number(event.target.value), page: 0 }, { preserveScroll: true })}>
                  {PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
                </select>
              </label>
            </div>
            <div className="catalog-settings__actions">
              <button type="button" className="text-button" onClick={() => { setFilters({}); setYearFilterReset(value => value + 1); updateUrl({ filters: {}, page: 0 }); }}>{labels.reset}</button>
              <button type="button" className="primary-button" onClick={() => { if (updateUrl({ filters, page: 0 })) setSettingsOpen(false); }}>{labels.apply}</button>
            </div>
          </div>
        )}

        {activeFilters.length > 0 && (
          <div className="active-filters" aria-label={labels.active}>
            <span>{labels.active}</span>
            {activeFilters.map(([key, value]) => (
              <button type="button" key={key} onClick={() => { const next = { ...filters, [key]: '' }; setFilters(next); updateUrl({ filters: next, page: 0 }); }}>
                {labels[key] || key}: {displayFilterValue(key, value)}<X size={13} />
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="catalog-results" aria-live="polite" aria-busy={status === 'loading' || status === 'updating'}>
        <header><span>{status === 'updating' ? labels.loading : labels.found}</span><strong>{showResults ? total : '—'}</strong></header>
        {status === 'loading' && <p className="archive-notice">{labels.loading}</p>}
        {status === 'error' && <p className="archive-notice archive-notice--error">{labels.error}</p>}
        {showResults && total === 0 && <p className="archive-notice">{labels.empty}</p>}

        {showResults && result.products.length > 0 && (
          <div className="catalog-group"><h2>{labels.productsGroup}<span>{result.productsPage?.totalElements || result.products.length}</span></h2>
            <div className="catalog-records">{result.products.map((item) => <RecordCard key={'p-' + item.id} item={item} product uploadHost={uploadHost} labels={labels} catalogReturnTo={catalogReturnTo} catalogReturnKey={location.key} />)}</div>
          </div>
        )}
        {showResults && result.details.length > 0 && (
          <div className="catalog-group"><h2>{labels.detailsGroup}<span>{result.detailsPage?.totalElements || result.details.length}</span></h2>
            <div className="catalog-records">{result.details.map((item) => <RecordCard key={'d-' + item.id} item={item} uploadHost={uploadHost} labels={labels} catalogReturnTo={catalogReturnTo} catalogReturnKey={location.key} />)}</div>
          </div>
        )}

        {showResults && <Pagination page={urlPage} totalPages={totalPages} onPage={(page) => updateUrl({ page })} labels={labels} />}
      </section>
    </main>
  );
}
